import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { suggestionSchema, type SuggestionFormData } from '@brent/shared/validation'
import type { SearchComposersResponse, GetComposerWorksResponse } from '@brent/shared/types'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form'
import { ComposerAutocompleteInput } from './ComposerAutocompleteInput'
import { PieceAutocompleteInput } from './PieceAutocompleteInput'
import type { Composer } from '../hooks/useComposerAutocomplete'
import type { Piece } from '../hooks/usePieceAutocomplete'

export type { SuggestionFormData }

interface SuggestionFormProps {
  defaultValues?: Partial<SuggestionFormData>
  onSubmit: (data: SuggestionFormData) => void
  onCancel?: () => void
  isLoading?: boolean
  submitLabel?: string
  commentPlaceholder?: string
  searchComposers?: (term: string, limit: number) => Promise<SearchComposersResponse>
  searchWorks?: (composerId: string, limit: number) => Promise<GetComposerWorksResponse>
}

export const SuggestionForm = ({
  defaultValues,
  onSubmit,
  onCancel,
  isLoading = false,
  submitLabel = 'Submit',
  commentPlaceholder = 'Why would you like to play this piece?',
  searchComposers,
  searchWorks,
}: SuggestionFormProps) => {
  // Track the selected composer ID and whether it's freetext
  const [selectedComposerId, setSelectedComposerId] = useState<string | null>(null)
  const [isComposerFreetext, setIsComposerFreetext] = useState<boolean>(false)

  // Update state when defaultValues change (for edit mode)
  useEffect(() => {
    if (defaultValues) {
      setSelectedComposerId(defaultValues.composer_wikidata_id || null)
      setIsComposerFreetext(!!(defaultValues.composer && !defaultValues.composer_wikidata_id))
    }
  }, [defaultValues])

  const form = useForm<SuggestionFormData>({
    resolver: zodResolver(suggestionSchema),
    defaultValues: {
      composer: defaultValues?.composer || '',
      title: defaultValues?.title || '',
      composer_wikidata_id: defaultValues?.composer_wikidata_id || null,
      work_wikidata_id: defaultValues?.work_wikidata_id || null,
      comment: defaultValues?.comment || '',
    },
  })

  // Handle composer selection
  const handleComposerSelect = (composer: Composer | null) => {
    const isFreetext =
      composer?.source === 'freetext' || !composer?.id || composer?.id === 'FREETEXT'
    setSelectedComposerId(composer?.id || null)
    setIsComposerFreetext(isFreetext)

    // Update the composer name in the form
    if (composer?.name) {
      form.setValue('composer', composer.name)
    }

    // Set the Wikidata ID in the form (null for freetext)
    // Map 'FREETEXT' marker to null
    const wikidataId = isFreetext || composer?.id === 'FREETEXT' ? null : composer?.id || null
    form.setValue('composer_wikidata_id', wikidataId)

    // Clear the title field when composer changes
    if (composer === null || form.getValues('composer') !== composer.name) {
      form.setValue('title', '')
      form.setValue('work_wikidata_id', null)
    }
  }

  // Handle piece selection
  const handlePieceSelect = (piece: Piece | null) => {
    const isFreetext = piece?.source === 'freetext' || !piece?.id || piece?.id === 'FREETEXT'

    // Update the piece title in the form (with catalog number if available)
    if (piece?.title) {
      const formattedTitle = piece.catalogNumber
        ? `${piece.title} (${piece.catalogNumber})`
        : piece.title
      form.setValue('title', formattedTitle)
    }

    // Set the work Wikidata ID in the form (null for freetext)
    // Map 'FREETEXT' marker to null
    const wikidataId = isFreetext || piece?.id === 'FREETEXT' ? null : piece?.id || null
    form.setValue('work_wikidata_id', wikidataId)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="composer"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Composer</FormLabel>
              <FormControl>
                <ComposerAutocompleteInput
                  ref={field.ref}
                  value={field.value}
                  onChange={field.onChange}
                  onComposerSelect={handleComposerSelect}
                  onBlur={field.onBlur}
                  disabled={isLoading}
                  placeholder="e.g., Ludwig van Beethoven"
                  searchFn={searchComposers}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <PieceAutocompleteInput
                  ref={field.ref}
                  value={field.value}
                  onChange={field.onChange}
                  onPieceSelect={handlePieceSelect}
                  onBlur={field.onBlur}
                  composerId={selectedComposerId}
                  isComposerFreetext={isComposerFreetext}
                  disabled={isLoading}
                  placeholder="e.g., Symphony No. 9"
                  searchFn={searchWorks}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="comment"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Comment (optional)</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder={commentPlaceholder}
                  disabled={isLoading}
                  rows={3}
                  maxLength={1000}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Submitting...' : submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  )
}
